/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AccessToken,
  assertUnreachable,
  HttpStatusCode,
  TalerCorebankApi,
  TalerErrorCode,
} from "@gnu-taler/taler-util";
import {
  AsyncButton,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { useState } from "preact/hooks";
import { useBankChallengeHandlerContext } from "../../context/challenge.js";
import { useSessionState } from "../../hooks/session.js";
import { OperationError } from "../../components/OperationError.js";
import { undefinedIfEmpty } from "../../utils.js";
import { ProfileNavigation } from "../ProfileNavigation.js";

export function UpdateAccountPassword({
  account: accountName,
  routeClose,
  onUpdateSuccess,

  routeMyAccountDetails,
  routeMyAccountMerchantIntegration,
  routeMyAccountPassword,
}: {
  routeClose: RouteDefinition;
  routeMyAccountDetails: RouteDefinition;
  routeMyAccountMerchantIntegration: RouteDefinition;
  routeMyAccountPassword: RouteDefinition;

  onUpdateSuccess: () => void;
  account: string;
}): VNode {
  const { i18n } = useTranslationContext();
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  const mfa = useBankChallengeHandlerContext();

  const [current, setCurrent] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [repeat, setRepeat] = useState<string | undefined>();

  const accountIsTheCurrentUser =
    credentials.status === "loggedIn"
      ? credentials.username === accountName
      : false;

  const errors = undefinedIfEmpty({
    current: !accountIsTheCurrentUser
      ? undefined
      : !current
        ? i18n.str`Required`
        : undefined,
    password: !password ? i18n.str`Required` : undefined,
    repeat: !repeat
      ? i18n.str`Required`
      : password !== repeat
        ? i18n.str`The passwords do not match.`
        : undefined,
  });
  const passwordChange = !password
    ? undefined
    : {
        old_password: current,
        new_password: password,
      };
  const { showError } = useNotificationContext();

  // i18n.str`update password`,
  const update = useNotifiedOperation<
    Awaited<ReturnType<typeof api.updatePassword>>,
    [string, AccessToken, TalerCorebankApi.AccountPasswordChange, string[]]
  >(
    (
      ct,
      username: string,
      token: AccessToken,
      request: TalerCorebankApi.AccountPasswordChange,
      challengeIds: string[],
    ) =>
      api.updatePassword({ username, token }, request, {
        challengeIds,
      }),
    {
      onSuccess: () => {
        mfa.cancel();
        // notifyInfo(i18n.str`Password changed`);
        onUpdateSuccess();
      },
      onFail: showError(
        i18n.str`Failed to update the password.`,
        (fail, username, token, request) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`Not authorized to change the password, maybe the session is invalid.`;
            case HttpStatusCode.NotFound:
              return i18n.str`Account not found`;
            case TalerErrorCode.BANK_NON_ADMIN_PATCH_MISSING_OLD_PASSWORD:
              return i18n.str`You need to provide the old password. If you don't have it contact your account administrator.`;
            case TalerErrorCode.BANK_PATCH_BAD_OLD_PASSWORD:
              return i18n.str`The current password is incorrect, so the password cannot be changed.`;
            case HttpStatusCode.Accepted:
              mfa.onNewChallenge(
                i18n.str`Password update`,
                username,
                fail.body,
                {
                  running: update.running,
                  cancel: update.cancel,
                  run: (challengeIds) =>
                    update.run(username, token, request, challengeIds),
                },
              );
              return undefined;
            case HttpStatusCode.Forbidden:
              return i18n.str`You don't have the rights to change the password.`;
            case TalerErrorCode.BANK_PASSWORD_TOO_SHORT:
              return i18n.str`The password is too short. Can't have less than 8 characters.`;
            case TalerErrorCode.BANK_PASSWORD_TOO_LONG:
              return i18n.str`The password is too long. Can't have more than 64 characters.`;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );
  return (
    <Fragment>
      {accountIsTheCurrentUser ? (
        <ProfileNavigation
          current="credentials"
          routeOverview={routeClose}
          routeMyAccountDetails={routeMyAccountDetails}
          routeMyAccountMerchantIntegration={routeMyAccountMerchantIntegration}
          routeMyAccountPassword={routeMyAccountPassword}
        />
      ) : undefined}

      <section class="mx-auto mt-6 max-w-2xl">
        <div>
          <h1 class="text-2xl font-semibold text-brand">
            <i18n.Translate>Update password</i18n.Translate>
          </h1>
          <p class="mt-2 text-sm text-gray-600">
            {accountIsTheCurrentUser ? (
              <i18n.Translate>
                Choose a new password for your bank account.
              </i18n.Translate>
            ) : (
              <i18n.Translate>
                Choose a new password for account "{accountName}".
              </i18n.Translate>
            )}
          </p>
        </div>
        <form
          class="mt-6 overflow-hidden rounded-xl border border-onBackground/10 bg-white shadow-sm"
          autoCapitalize="none"
          autoCorrect="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div class="px-4 py-6 sm:p-8">
            <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              {accountIsTheCurrentUser ? (
                <div class="sm:col-span-5">
                  <label
                    class="block text-sm font-medium leading-6 text-onBackground"
                    for="current-password"
                  >
                    {i18n.str`Current password`}
                    <b class="text-[red]"> *</b>
                  </label>
                  <div class="mt-2">
                    <input
                      type="password"
                      class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                      name="current"
                      id="current-password"
                      data-error={!!errors?.current && current !== undefined}
                      value={current ?? ""}
                      onChange={(e) => {
                        setCurrent(e.currentTarget.value);
                      }}
                      autocomplete="current-password"
                    />
                    <ShowInputErrorLabel
                      message={errors?.current}
                      isDirty={current !== undefined}
                    />
                  </div>
                  <p class="mt-2 text-sm text-gray-500">
                    <i18n.Translate>
                      Your current password, for security
                    </i18n.Translate>
                  </p>
                </div>
              ) : undefined}

              <div class="sm:col-span-5">
                <label
                  class="block text-sm font-medium leading-6 text-onBackground"
                  for="password"
                >
                  {i18n.str`New password`}
                  <b class="text-[red]"> *</b>
                </label>
                <div class="mt-2">
                  <input
                    type="password"
                    class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    name="password"
                    id="password"
                    data-error={!!errors?.password && password !== undefined}
                    value={password ?? ""}
                    onChange={(e) => {
                      setPassword(e.currentTarget.value);
                    }}
                    autocomplete="new-password"
                  />
                  <ShowInputErrorLabel
                    message={errors?.password}
                    isDirty={password !== undefined}
                  />
                </div>
              </div>

              <div class="sm:col-span-5">
                <label
                  class="block text-sm font-medium leading-6 text-onBackground"
                  for="repeat"
                >
                  {i18n.str`Confirm new password`}
                  <b class="text-[red]"> *</b>
                </label>
                <div class="mt-2">
                  <input
                    type="password"
                    class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    name="repeat"
                    id="repeat"
                    data-error={!!errors?.repeat && repeat !== undefined}
                    value={repeat ?? ""}
                    onChange={(e) => {
                      setRepeat(e.currentTarget.value);
                    }}
                    // placeholder=""
                    autocomplete="new-password"
                  />
                  <ShowInputErrorLabel
                    message={errors?.repeat}
                    isDirty={repeat !== undefined}
                  />
                </div>
                <p class="mt-2 text-sm text-gray-500">
                  <i18n.Translate>Enter the new password again.</i18n.Translate>
                </p>
              </div>
            </div>
          </div>
          <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                href={routeClose.url({})}
                data-name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              <AsyncButton
                submit
                name="change"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={!passwordChange || !token || !!errors}
                onClick={() =>
                  update.run(accountName, token!, passwordChange!, [])
                }
              >
                <i18n.Translate>Update password</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </form>
      </section>
    </Fragment>
  );
}
